import type {
  CliAdaptBlockFromLibraryData,
  CliAdaptBlockFromLibraryRequest,
  CliLibraryAdaptationPreflightData,
  CliLibraryAdaptationPreflightRequest,
} from '@modules/shared-types';

import { builderAuthHeader, getBuilderApiBaseUrl } from './builderApiClient';

export class LibraryAdaptationApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly field?: string,
  ) {
    super(message);
    this.name = 'LibraryAdaptationApiError';
  }
}

export class LibraryAdaptationService {
  constructor(
    private readonly projectId: string,
    private readonly strategyId: string,
  ) {}

  preflight(
    blockId: string,
    request: CliLibraryAdaptationPreflightRequest,
  ): Promise<CliLibraryAdaptationPreflightData> {
    return this.post(blockId, 'preflight', request);
  }

  adapt(
    blockId: string,
    request: CliAdaptBlockFromLibraryRequest,
  ): Promise<CliAdaptBlockFromLibraryData> {
    return this.post(blockId, 'adapt', request);
  }

  private async post<T>(
    blockId: string,
    operation: 'preflight' | 'adapt',
    body: unknown,
  ): Promise<T> {
    const headers = await builderAuthHeader();
    const response = await fetch(
      `${getBuilderApiBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(this.projectId)}`
      + `/strategies/${encodeURIComponent(this.strategyId)}/blocks/${encodeURIComponent(blockId)}`
      + `/library-adaptation/${operation}`,
      {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = payload?.error ?? payload;
      const field = Array.isArray(error?.errors) && typeof error.errors[0]?.field === 'string'
        ? error.errors[0].field
        : undefined;
      throw new LibraryAdaptationApiError(
        error?.detail || error?.message || `HTTP ${response.status}`,
        response.status,
        error?.code,
        field,
      );
    }
    return (payload?.data ?? payload) as T;
  }
}
